import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Search, RefreshCw, ArrowRightCircle, Lock, Unlock, Crown, Trash2, AlertTriangle, Gift, RotateCcw, SlidersHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import SuperAdminAnalytics from "@/pages/admin/SuperAdminAnalytics";

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  trialing: "bg-sky-100 text-sky-800 border-sky-200",
  canceled_at_period_end: "bg-amber-100 text-amber-800 border-amber-200",
  none: "bg-slate-100 text-slate-700 border-slate-200",
  expired: "bg-red-100 text-red-700 border-red-200",
};

function StatusPill({ status, isActive }) {
  const tone = STATUS_STYLES[status] || STATUS_STYLES.none;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${tone}`}
      data-testid={`status-pill-${status || "none"}`}
    >
      {isActive ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
      {(status || "none").replace(/_/g, " ")}
    </span>
  );
}

export default function SuperAdminTenants() {
  const { reloadImpersonation } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");

  // --- Purge test data state ---
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePlan, setPurgePlan] = useState(null);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super/tenants");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
    // `api`, `toast`, `formatApiError` are stable imports; setState fns are
    // stable. Nothing here changes across renders — deliberately empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const switchTo = async (cid) => {
    setBusyId(cid);
    try {
      await api.post("/super/switch-tenant", { company_id: cid });
      await reloadImpersonation();
      toast.success(`Now viewing tenant: ${cid}`);
      // hard-reload so every cached resource (dashboard, payroll, etc.) re-fetches under the new tenant
      setTimeout(() => { window.location.href = "/admin"; }, 300);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId("");
    }
  };

  const clearOverride = async () => {
    try {
      await api.post("/super/clear-tenant");
      await reloadImpersonation();
      toast.success("Tenant override cleared");
      setTimeout(() => { window.location.href = "/admin"; }, 300);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggleComp = async (tenant) => {
    const want = !tenant.comped;
    const verb = want ? "Comp" : "Un-comp";
    const msg = want
      ? `Grant complimentary access to "${tenant.name}"? They'll bypass all subscription billing.`
      : `Revoke complimentary access for "${tenant.name}"? They'll need an active subscription to use premium features again.`;
    if (!window.confirm(msg)) return;
    setBusyId(tenant.id);
    try {
      await api.post("/super/comp-tenant", { company_id: tenant.id, comped: want });
      toast.success(`${verb}ed ${tenant.name}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId("");
    }
  };

  // Force-pull this tenant's subscription state directly from Stripe.
  // Bypasses webhooks — use when the webhook was down / mis-configured and
  // a paying tenant is stuck showing an out-of-date status in our DB.
  const syncStripe = async (tenant) => {
    setBusyId(tenant.id);
    try {
      const { data } = await api.post(`/super/tenants/${tenant.id}/sync-stripe`);
      const before = data.before?.subscription_status || "none";
      const after = data.after?.subscription_status || "none";
      if (data.changed) {
        toast.success(`${tenant.name}: ${before} → ${after}${data.after?.plan ? ` · ${data.after.plan}` : ""}`);
      } else {
        toast.info(`${tenant.name} is already ${after} — Stripe agrees, no change.`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId("");
    }
  };

  // Manually override status. Used for support edge cases (webhook down,
  // want to lock a bad-actor tenant, etc.). This overwrites our cached
  // copy; a subsequent webhook / Sync will overwrite whatever we set.
  const setStatusManually = async (tenant, status) => {
    const nice = { active: "Active", trialing: "Trialing", canceled: "Canceled", past_due: "Past due", none: "None" }[status];
    if (!window.confirm(`Manually set "${tenant.name}" status to ${nice}?\n\nThis does NOT touch Stripe — the next webhook or Sync will overwrite it.`)) return;
    setBusyId(tenant.id);
    try {
      await api.post("/super/set-status", { company_id: tenant.id, status });
      toast.success(`${tenant.name} status → ${nice}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId("");
    }
  };

  // Per-tenant delete. Two-step type-to-confirm dialog handled by the
  // deleteConfirm state below.
  const [deleteTarget, setDeleteTarget] = useState(null); // {tenant} or null
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const runDeleteTenant = async () => {
    if (!deleteTarget) return;
    if (deleteConfirm.trim().toLowerCase() !== (deleteTarget.name || "").trim().toLowerCase()) {
      toast.error(`Type "${deleteTarget.name}" exactly to confirm.`);
      return;
    }
    setDeleteBusy(true);
    try {
      const { data } = await api.post("/super/delete-tenant", {
        company_id: deleteTarget.id,
        confirm_name: deleteConfirm,
      });
      const counts = data.deleted_counts || {};
      const totalRows = Object.values(counts).reduce((s, n) => s + (Number(n) || 0), 0);
      toast.success(`Deleted "${data.name}" — ${totalRows} rows removed across ${Object.keys(counts).length} collections.`);
      setDeleteTarget(null);
      setDeleteConfirm("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDeleteBusy(false);
    }
  };

  // ---- Purge test data ----
  const openPurge = async () => {
    setPurgeOpen(true);
    setPurgeConfirm("");
    setPurgeLoading(true);
    try {
      const { data } = await api.get("/super/purge-test-data/preview");
      setPurgePlan(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setPurgeOpen(false);
    } finally {
      setPurgeLoading(false);
    }
  };

  const runPurge = async () => {
    if (purgeConfirm.trim().toUpperCase() !== "DELETE TEST DATA") {
      toast.error("Type DELETE TEST DATA to confirm.");
      return;
    }
    setPurgeBusy(true);
    try {
      const { data } = await api.post("/super/purge-test-data", { confirm: purgeConfirm });
      const counts = data.deleted_counts || {};
      const totalRows = Object.values(counts).reduce((s, n) => s + (Number(n) || 0), 0);
      toast.success(`Test data purged — ${totalRows} rows removed across ${Object.keys(counts).length} collections.`);
      setPurgeOpen(false);
      setPurgeConfirm("");
      setPurgePlan(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPurgeBusy(false);
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!q.trim()) return data.tenants;
    const needle = q.toLowerCase();
    return data.tenants.filter((t) =>
      [t.id, t.name, t.owner_email, t.plan, t.subscription_status].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [data, q]);

  if (!data) {
    return <div className="py-20 text-center text-slate-500" data-testid="tenants-loading">Loading tenants…</div>;
  }

  return (
    <div className="space-y-5" data-testid="super-tenants-page">
      <SuperAdminAnalytics />
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Crown className="h-3.5 w-3.5 text-amber-500" /> Super Admin
          </p>
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Tenants</h1>
          <p className="text-sm text-slate-500 mt-1">Every company on the platform. Click &quot;View as&quot; to impersonate a tenant — every admin page will scope to that company until you switch back.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="tenants-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={clearOverride} data-testid="tenants-clear-override-btn">
            Clear override
          </Button>
          <Button
            size="sm"
            onClick={openPurge}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="purge-test-data-btn"
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete Test Data
          </Button>
        </div>
      </header>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Search className="h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, id, owner email, plan, status…"
              className="h-9"
              data-testid="tenants-search-input"
            />
            <span className="text-xs text-slate-500 whitespace-nowrap">{filtered.length} of {data.count}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tenants-table">
              <thead>
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-2">Tenant</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2">Plan</th>
                  <th className="py-2 px-2">Owner</th>
                  <th className="py-2 px-2 text-right">Active users</th>
                  <th className="py-2 px-2">Trial / period end</th>
                  <th className="py-2 pl-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tenant-row-${t.id}`}>
                    <td className="py-2 pr-2">
                      <div className="font-bold text-slate-900 flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 text-slate-400" /> {t.name}
                        {t._legacy && (
                          <span className="text-[9px] font-bold uppercase text-amber-600 ml-1">legacy</span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono">{t.id}</div>
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex flex-col gap-0.5 items-start">
                        <StatusPill status={t.subscription_status} isActive={t.is_active} />
                        {t.comped && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200" data-testid={`comped-pill-${t.id}`}>
                            <Gift className="h-2.5 w-2.5" /> Comped
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-slate-700 capitalize">
                      {t.plan ? <>{t.plan} <span className="text-slate-400">· {t.interval || "monthly"}</span></> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-2 text-slate-700 text-[12px]">{t.owner_email || <span className="text-slate-400">—</span>}</td>
                    <td className="py-2 px-2 text-right font-mono text-slate-700">{t.active_employees}</td>
                    <td className="py-2 px-2 text-slate-700 text-[12px]">
                      {t.period_end ? fmtDate(t.period_end.slice(0, 10)) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 pl-2 text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        {t.stripe_customer_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => syncStripe(t)}
                            disabled={busyId === t.id}
                            className="border-sky-300 text-sky-800 hover:bg-sky-50"
                            data-testid={`sync-stripe-${t.id}`}
                            title="Pull the current subscription state from Stripe (use when webhooks missed an event)"
                          >
                            <RotateCcw className={`h-3.5 w-3.5 mr-1 ${busyId === t.id ? "animate-spin" : ""}`} />
                            Sync
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleComp(t)}
                          disabled={busyId === t.id}
                          className={
                            t.comped
                              ? "border-fuchsia-300 text-fuchsia-800 hover:bg-fuchsia-50"
                              : "border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                          }
                          data-testid={`comp-toggle-${t.id}`}
                          title={t.comped ? "Revoke complimentary access (tenant will need to subscribe)" : "Grant complimentary access (bypass subscription billing)"}
                        >
                          <Gift className="h-3.5 w-3.5 mr-1" />
                          {t.comped ? "Un-comp" : "Comp"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => switchTo(t.id)}
                          disabled={busyId === t.id}
                          className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
                          data-testid={`view-as-${t.id}`}
                        >
                          <ArrowRightCircle className="h-3.5 w-3.5 mr-1" /> {busyId === t.id ? "…" : "View as"}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyId === t.id}
                              className="border-slate-300 text-slate-700 hover:bg-slate-100 px-2"
                              data-testid={`row-menu-${t.id}`}
                              title="More actions"
                            >
                              <SlidersHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                              Manually set status
                            </DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => setStatusManually(t, "active")} data-testid={`set-active-${t.id}`}>
                              <Unlock className="h-3.5 w-3.5 mr-2 text-emerald-600" /> Active
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setStatusManually(t, "trialing")} data-testid={`set-trialing-${t.id}`}>
                              <Unlock className="h-3.5 w-3.5 mr-2 text-sky-600" /> Trialing
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setStatusManually(t, "canceled")} data-testid={`set-canceled-${t.id}`}>
                              <Lock className="h-3.5 w-3.5 mr-2 text-amber-600" /> Canceled (locks out)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setStatusManually(t, "none")} data-testid={`set-none-${t.id}`}>
                              <Lock className="h-3.5 w-3.5 mr-2 text-slate-500" /> None (reset)
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => { setDeleteTarget(t); setDeleteConfirm(""); }}
                              className="text-red-700 focus:text-red-800 focus:bg-red-50"
                              data-testid={`delete-tenant-${t.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete tenant…
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-10 text-center text-slate-400">No tenants match.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Delete single tenant dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleteBusy) { setDeleteTarget(null); setDeleteConfirm(""); } }}>
        <DialogContent className="max-w-lg" data-testid="delete-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Delete tenant
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="text-sm space-y-3">
              <p>
                You&apos;re about to <b className="text-red-700">permanently delete</b> the tenant{" "}
                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{deleteTarget.name}</span>.
              </p>
              <p className="text-slate-700">
                Every user, submission, punch, paystub, job entry, and audit log tied to this tenant will be wiped
                across all collections. Any Stripe subscription remains untouched in Stripe — cancel it there separately.
              </p>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 font-mono">
                <div>id: {deleteTarget.id}</div>
                <div>active users: {deleteTarget.active_employees}</div>
                <div>status: {deleteTarget.subscription_status || "none"}</div>
                <div>owner: {deleteTarget.owner_email || "—"}</div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Type the tenant name to confirm
                </label>
                <Input
                  autoFocus
                  placeholder={deleteTarget.name}
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  className="mt-1"
                  data-testid="delete-tenant-confirm-input"
                  onKeyDown={(e) => { if (e.key === "Enter") runDeleteTenant(); }}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirm(""); }} disabled={deleteBusy} data-testid="delete-tenant-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={runDeleteTenant}
              disabled={deleteBusy || !deleteTarget || (deleteConfirm.trim().toLowerCase() !== (deleteTarget?.name || "").trim().toLowerCase())}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-tenant-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> {deleteBusy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Test Data dialog */}
      <Dialog open={purgeOpen} onOpenChange={(o) => { if (!purgeBusy) setPurgeOpen(o); }}>
        <DialogContent className="max-w-2xl" data-testid="purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Delete all test data
            </DialogTitle>
          </DialogHeader>

          {purgeLoading ? (
            <p className="py-8 text-center text-slate-500" data-testid="purge-loading">Analyzing database…</p>
          ) : !purgePlan ? null : (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
                This is a one-way delete. The primary tenant <strong data-testid="purge-primary">{purgePlan.primary_tenant}</strong>, all super-admin accounts, and your own user row are always preserved. Everything else listed below will be wiped — companies, users, time entries, jobs, paystubs, audit logs, the lot.
              </div>

              <div>
                <p className="font-semibold text-slate-900 mb-1.5">
                  Tenants to delete{" "}
                  <span className="text-xs font-mono text-slate-500" data-testid="purge-tenants-count">
                    ({purgePlan.tenants_to_delete.length})
                  </span>
                </p>
                {purgePlan.tenants_to_delete.length === 0 ? (
                  <p className="text-slate-500 text-xs">None.</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100 text-xs">
                    {purgePlan.tenants_to_delete.map((t) => (
                      <div key={t.id} className="px-3 py-1.5 flex items-center justify-between" data-testid={`purge-tenant-${t.id}`}>
                        <span><span className="font-semibold">{t.name}</span> <span className="text-slate-400 font-mono">· {t.id}</span></span>
                        <span className="text-slate-500">{t.users} user{t.users === 1 ? "" : "s"} · {t.subscription_status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="font-semibold text-slate-900 mb-1.5">
                  Test users inside <span className="font-mono">{purgePlan.primary_tenant}</span>{" "}
                  <span className="text-xs font-mono text-slate-500" data-testid="purge-users-count">
                    ({purgePlan.primary_test_users.length})
                  </span>
                </p>
                {purgePlan.primary_test_users.length === 0 ? (
                  <p className="text-slate-500 text-xs">None.</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100 text-xs">
                    {purgePlan.primary_test_users.map((u) => (
                      <div key={u.id} className="px-3 py-1.5 flex items-center justify-between" data-testid={`purge-user-${u.id}`}>
                        <span><span className="font-semibold">{u.name || "—"}</span> <span className="text-slate-400">· {u.email}</span></span>
                        <span className="text-slate-500 uppercase tracking-wider">{u.role}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                  Type <span className="font-mono text-red-700">DELETE TEST DATA</span> to confirm
                </label>
                <Input
                  value={purgeConfirm}
                  onChange={(e) => setPurgeConfirm(e.target.value)}
                  placeholder="DELETE TEST DATA"
                  data-testid="purge-confirm-input"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPurgeOpen(false)} disabled={purgeBusy} data-testid="purge-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={runPurge}
              disabled={
                purgeBusy ||
                purgeLoading ||
                !purgePlan ||
                purgeConfirm.trim().toUpperCase() !== "DELETE TEST DATA"
              }
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="purge-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> {purgeBusy ? "Deleting…" : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
